import * as React from 'react';
// import Button from '@mui/material/Button';
import styled from '@emotion/styled';
// import Dialog from '@mui/material/Dialog';
// import DialogTitle from '@mui/material/DialogTitle';
// import DialogContent from '@mui/material/DialogContent';
// import DialogActions from '@mui/material/DialogActions';
// import Typography from '@mui/material/Typography';
import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  // Divider,
  Typography,
  useTheme,
} from '@material-ui/core';
import useGenericStyles from 'src/styles/useGenericStyles';
import { FileDropzone } from '../fileUpload/FileDropZoneCircular';
import { AccountInformationForm } from 'src/components/account/AccountInformationForm';
import { useFormik } from 'formik';
import { isEmpty } from 'lodash';
import toast from 'react-hot-toast';
import { CustomButton2 } from '../buttons/CustomButton2';
import { useAppSelector } from 'src/hooks/useRedux';
import * as Yup from 'yup';
import type { FileRejection } from 'react-dropzone';
import axiosInstance from 'src/utils/axios';
import { userApi } from 'src/api/userApi';

type CustomDialogProps = {
  isOpen: boolean;
  handleCloseDialog: any;
  rowData?: any;
  updateData?: any;
};

const BootstrapDialog = styled(Dialog)(() => ({
  '& .MuiDialogContent-root': {
    padding: '20px',
    maxWidth: '790px',
  },
  '& .MuiPaper-root': {
    borderRadius: '16px',
  },
  '& .MuiDialogActions-root': {
    padding: '20px',
  },
  '& .MuiInputBase-root': {
    fontSize: '12px'
  },
  '& .MuiFormLabel-root': {
    fontSize: '12px', // label font
  },
  '& .MuiAutocomplete-option': {
    fontSize: '12px', // dropdown list font
  },

}));

export default function CustomDialog({
  isOpen,
  handleCloseDialog,
  rowData,
  updateData,
}: CustomDialogProps) {
  const [loading, setLoading] = React.useState(false);
  const gettingRoles = useAppSelector((state) => state.roles.loading);
  const roles = useAppSelector((state) => state.roles.allRoles);
  const classes = useGenericStyles();
  const theme = useTheme();
  const formFields = [
    {
      type: 'text',
      label: 'First Name',
      name: 'firstName',
      placeholder: 'First Name',
    },
    {
      type: 'text',
      label: 'Last Name',
      name: 'lastName',
      placeholder: 'Last Name',
    },
    {
      type: 'text',
      label: 'Email',
      name: 'email',
      placeholder: 'Email',
    },
    {
      type: 'text',
      label: 'Designation',
      name: 'designation',
      placeholder: 'Designation',
    },
    {
      type: 'text',
      label: 'Phone Number',
      name: 'phoneNumber',
      placeholder: 'Phone Number',
    },
    {
      type: 'dropdown',
      label: 'Role',
      name: 'role',
      placeholder: 'Roles',
      options: roles,
      loading: gettingRoles,
      limitTags: 1,
    },
    {
      type: 'text',
      label: 'About',
      name: 'info',
      placeholder: 'About',
      xs: 12,
      maxLength: 255,
    },
  ];

  const formik = useFormik({
    initialValues: {
      firstName: rowData?.firstName || '',
      lastName: rowData?.lastName || '',
      // userName: '',
      email: rowData?.email || '',
      designation: rowData?.designation || '',
      phoneNumber: rowData?.contactNumber?.[0] || '',
      info: rowData?.info || '',
      avatar: rowData?.avatar || '',
      role: rowData?.roles || [],
    },
    validationSchema: Yup.object().shape({
      firstName: Yup.string().trim().required('Field required'),
      lastName: Yup.string().trim().required('Field required'),
      // userName: Yup.string().trim().required('Field required'),
      email: Yup.string()
        .trim()
        .required('Field required')
        .email('Invalid Email'),
      designation: Yup.string().trim().required('Field required'),
      phoneNumber: Yup.string().trim().required('Field required'),
      role: Yup.array().min(1, 'At least one role is required').required('Field required'),
      info: Yup.string().trim().max(255, 'Maximum 255 characters allowed'),
      // avatar: Yup.string().trim().required('Field'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      const payload: any = {
        avatar: values.avatar,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        designation: values.designation,
        contactNumber: values.phoneNumber,
        info: values.info,
        roleId: values?.role?.map((role) => role.id),
      };
      try {
        setSubmitting(true);
        let response;
        if (isEmpty(rowData)) {
          response = await userApi.createUser(payload);
        } else {
          response = await userApi.edit(rowData.id, payload);
        }
        if (response.status === 201) {
          toast.success('Created Successfully');
          setSubmitting(false);
        }
        if (!isEmpty(rowData)) {
          toast.success('Updated Successfully');
          setSubmitting(false);
        }
        handleCloseDialog.handleClose();
        updateData();
      } catch (error) {
        setSubmitting(false);
        toast.error(error?.response?.data?.message || 'Something went wrong');
      }
    },
  });

  const uploadImage = (file) => {
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    axiosInstance
      .post(`${process.env.REACT_APP_IMAGE_UPLOAD_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Access-Control-Allow-Origin': '*',
        },
      })
      .then((response) => {
        if (response?.status === 201) {
          setLoading(false);
          formik.setFieldValue('avatar', response?.data?.blob_url);
          toast.success('Attachments uploaded successfully');
        }
      })
      .catch((error) => {
        setLoading(false);
        toast.error(error);
      });
  };

  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  const handleDrop = (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    fileRejections.forEach((rejection) => {
      const oversized = rejection.errors.some(
        (error) => error.code === 'file-too-large'
      );
      if (oversized) {
        toast.error('Maximum file size is 10MB');
      } else {
        toast.error(
          rejection.errors?.[0]?.message || 'Unable to upload the selected file'
        );
      }
    });

    acceptedFiles.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error('Maximum file size is 10MB');
        return;
      }
      uploadImage(file);
    });
  };
  // const [open, setOpen] = React.useState(false);

  // const handleClickOpen = () => {
  //   setOpen(true);
  // };
  // const handleClose = () => {
  //   setOpen(false);
  // };

  // Use useEffect to set formik values when component mounts or rowData changes
  React.useEffect(() => {
    if (rowData) {
      formik.setValues({
        firstName: rowData.firstName || '',
        lastName: rowData.lastName || '',
        email: rowData.email || '',
        designation: rowData.designation || '',
        phoneNumber: rowData.contactNumber?.[0] || '',
        info: rowData.info || '',
        avatar: rowData.avatar || '',
        role: roles.filter((role) => rowData.roles.includes(role.id)) || '',
      });
    }
  }, [rowData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    formik.handleSubmit();
  };

  return (
    <>
      <BootstrapDialog
        onClose={handleCloseDialog.handleClose}
        aria-labelledby="customized-dialog-title"
        open={isOpen}>
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          <Typography className={classes.smallHeading}>
            {!isEmpty(rowData) ? 'Update' : 'Create new'} contact
          </Typography>
        </DialogTitle>
        {/* <Divider
          sx={{
            // marginBottom: '20px',
            color: '#EDF2F7',
          }}
        /> */}
        <DialogContent dividers>
          {/* <Typography className={classes.smallHeading}>
            Create new contact
          </Typography> */}

          <Box
            sx={{
              display: 'flex',
              width: '100%',
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              boxShadow:
                '0px 3px 8px -1px rgba(50, 50, 71, 0.05),  0px 0px 1px 0px rgba(12, 26, 75, 0.24)',
              padding: '20px',
            }}>
            <Avatar
              sx={{
                width: '48px',
                height: '48px',
              }}
              src={formik.values.avatar}>
              {loading ? <CircularProgress color="primary" /> : null}
            </Avatar>
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                justifyContent: 'space-between',
                marginLeft: '10px',
              }}>
              <Box>
                <Typography className={classes.heading}>
                  {rowData?.firstName}  {rowData?.lastName}
                </Typography>
                <Typography className={classes.description}>
                  PNG or JPG no bigger than 10MB
                </Typography>
              </Box>
              {/* <Button className={classes.button} startIcon={<Upload />}>
            Upload
          </Button> */}
              <FileDropzone
                accept={'image/*'}
                // files={files}
                maxFiles={1}
                maxSize={10 * 1024 * 1024}
                onDrop={handleDrop}
              />
            </Box>
          </Box>
          <Box
            sx={{
              marginTop: '20px',
            }}>
            <Typography className={classes.heading}>
              Account Information
            </Typography>
          </Box>
          <AccountInformationForm
            setFieldValue={formik.setFieldValue}
            formFields={formFields}
            rootStyles={{
              margin: '20px 0 ',
            }}
            formikErrors={formik.errors}
            formikValues={formik.values}
            handleChange={formik.handleChange}
          />
        </DialogContent>
        <DialogActions>
          {/* <Button autoFocus onClick={handleClose}>
            Save changes
          </Button> */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
            }}>
            <CustomButton2
              // backgroundColor={theme.palette.primary.main}
              sx={{
                color: 'black',
                boxShadow:
                  '0px 3px 8px -1px rgba(50, 50, 71, 0.05), 0px 0px 1px 0px rgba(12, 26, 75, 0.24)',
              }}
              className={classes.button}
              onClick={handleCloseDialog.handleClose}>
              Cancel
            </CustomButton2>
            <CustomButton2
              backgroundColor={theme.palette.primary.main}
              className={classes.button}
              onClick={(e) => handleSubmit(e)}
              type="submit"
              disabled={formik.isSubmitting || !formik.isValid}>
              {formik.isSubmitting ? (
                <CircularProgress
                  sx={{
                    color: '#FFFFFF',
                  }}
                  size="20px"
                />
              ) : (
                'Save Changes'
              )}
            </CustomButton2>
          </Box>
        </DialogActions>
      </BootstrapDialog>
    </>
  );
}
